import { Globe } from 'lucide-react';
import { useDroppable } from '@dnd-kit/core';
import { format } from 'date-fns';
import type { ShiftListItemDto, Task, CalendarEventDto, CampaignCalendarVisibility } from '@signalvote/shared';
import { cn } from '@/lib/utils';
import type { KeyDateItem } from '@/lib/keyDates';
import { KeyDateChip } from './KeyDateChip';
import { formatMonthDay, isToday, formatTime12Hour, toTimeString, toTimeStringInTimezone } from '@/lib/calendarUtils';

/** Maximum number of events to show before "+X more" */
const MAX_VISIBLE_EVENTS = 4;

interface MonthDayCellProps {
  /** The date this cell represents */
  date: Date;
  /** Click handler to navigate to week view */
  onClick: () => void;
  /** Shifts for this day */
  shifts: ShiftListItemDto[];
  /** Tasks for this day */
  tasks: Task[];
  /** External calendar events (Google/Microsoft) for this day */
  calendarEvents?: CalendarEventDto[];
  keyDates?: KeyDateItem[];
  /** Campaign timezone for displaying calendar event times */
  timeZoneId?: string;
  /** Whether this date is in the currently displayed month */
  isCurrentMonth?: boolean;
}

type EventType = 'shift' | 'task' | 'calendar-event';

function getEventColorClass(type: EventType): string {
  switch (type) {
    case 'shift':
      return 'bg-emerald-100 text-emerald-800';
    case 'task':
      return 'bg-amber-100 text-amber-800';
    case 'calendar-event':
      return '';
  }
}

interface EventItemProps {
  title: string;
  time?: string;
  type: EventType;
  calendarColor?: string;
  isOtherCampaign?: boolean;
  sourceCampaignName?: string | null;
  visibility?: CampaignCalendarVisibility | null;
}

function EventItem({ title, time, type, calendarColor, isOtherCampaign, sourceCampaignName, visibility }: EventItemProps) {
  const isShowBusy = isOtherCampaign && visibility === 'ShowBusy';
  const displayTitle = isShowBusy ? 'Busy' : title;
  const prefix = isOtherCampaign && !isShowBusy && sourceCampaignName
    ? `${sourceCampaignName}: `
    : '';

  return (
    <div
      className={cn(
        'text-[11px] leading-tight px-1.5 py-0.5 rounded truncate',
        getEventColorClass(type),
        isOtherCampaign && 'opacity-35'
      )}
      style={calendarColor ? {
        backgroundColor: `${calendarColor}20`,
        color: calendarColor,
      } : undefined}
    >
      <span className="truncate">
        {type === 'task' && <span className="mr-1">☐</span>}
        {type === 'calendar-event' && <Globe className="inline h-2.5 w-2.5 mr-1 -mt-px" />}
        {prefix}{displayTitle}
      </span>
      {time && (
        <span className="ml-1 opacity-75 text-[10px]">{time}</span>
      )}
    </div>
  );
}

/**
 * Individual day cell for the month view grid.
 * - Shows day number with today highlighted
 * - Shows stacked event items (shifts, tasks, and calendar events)
 * - Shows "+X more" if too many events
 * - Muted styling for days outside current month
 */
export function MonthDayCell({
  date,
  onClick,
  shifts,
  tasks,
  calendarEvents = [],
  keyDates = [],
  timeZoneId,
  isCurrentMonth = true,
}: MonthDayCellProps) {
  const isTodayDate = isToday(date);
  const dateStr = format(date, 'yyyy-MM-dd');

  // Droppable setup for drag-from-sidebar
  const { setNodeRef, isOver } = useDroppable({
    id: `month-${dateStr}`,
    data: { date: dateStr, type: 'month-cell' },
  });

  // Combine all event types and sort by time
  const events: Array<{
    id: string;
    title: string;
    time?: string;
    sortTime: string;
    type: EventType;
    calendarColor?: string;
    isOtherCampaign?: boolean;
    sourceCampaignName?: string | null;
    visibility?: CampaignCalendarVisibility | null;
  }> = [
    ...shifts.map((shift) => ({
      id: shift.id,
      title: shift.walkbookName || shift.name || 'Shift',
      time: shift.startTime ? formatTime12Hour(shift.startTime) : undefined,
      sortTime: shift.startTime || '00:00',
      type: 'shift' as const,
    })),
    ...tasks.map((task) => ({
      id: task.id,
      title: task.title,
      time: task.dueTime ? formatTime12Hour(task.dueTime) : undefined,
      sortTime: task.dueTime || '23:59',
      type: 'task' as const,
    })),
    ...calendarEvents.map((evt) => {
      const timeStr = evt.isAllDay ? undefined : toTimeStringInTimezone(evt.startTime, timeZoneId);
      return {
        id: evt.id,
        title: evt.title,
        time: timeStr ? formatTime12Hour(timeStr) : undefined,
        sortTime: timeStr || '00:00',
        type: 'calendar-event' as const,
        calendarColor: evt.calendarColor || '#6366f1',
        isOtherCampaign: evt.isOtherCampaign,
        sourceCampaignName: evt.sourceCampaignName,
        visibility: evt.visibility,
      };
    }),
  ].sort((a, b) => a.sortTime.localeCompare(b.sortTime));

  const eventSlots = Math.max(0, MAX_VISIBLE_EVENTS - keyDates.length);
  const visibleEvents = events.slice(0, eventSlots);
  const hiddenCount = events.length - eventSlots;

  return (
    <button
      ref={setNodeRef}
      type="button"
      onClick={onClick}
      className={cn(
        'p-1 border-b border-r border-dotted border-gray-200 hover:bg-gray-50 transition-colors text-left flex flex-col overflow-hidden',
        !isCurrentMonth && 'text-gray-300 bg-gray-50/50',
        isOver && 'ring-2 ring-blue-300 bg-blue-50'
      )}
    >
      {/* Day number */}
      <span
        className={cn(
          'inline-flex items-center justify-center w-6 h-6 text-xs font-medium mb-0.5',
          isTodayDate && 'bg-blue-600 text-white rounded-full'
        )}
      >
        {formatMonthDay(date)}
      </span>

      {/* Stacked events */}
      <div className="flex flex-col gap-0.5 min-h-0 overflow-hidden">
        {keyDates.length > 0 && (
          <div role="list" className="flex flex-col gap-0.5">
            {keyDates.map((item) => (
              <KeyDateChip key={item.key} item={item} className="text-[11px]" />
            ))}
          </div>
        )}
        {visibleEvents.map((event) => (
          <EventItem
            key={event.id}
            title={event.title}
            time={event.time}
            type={event.type}
            calendarColor={event.calendarColor}
            isOtherCampaign={event.isOtherCampaign}
            sourceCampaignName={event.sourceCampaignName}
            visibility={event.visibility}
          />
        ))}
        {hiddenCount > 0 && (
          <span className="text-[10px] text-gray-500 px-1">
            +{hiddenCount} more
          </span>
        )}
      </div>
    </button>
  );
}

export default MonthDayCell;
